import React from 'react'
import { Search, Bell, Sun, Moon, LogOut, User, Settings, ChevronDown } from 'lucide-react'
import { useThemeStore } from '@/stores/themeStore'
import { useUserStore, initialsForName } from '@/stores/userStore'
import { useNotificationStore } from '@/stores/notificationStore'
import { useAuth0 } from '@auth0/auth0-react'
import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'

const TYPE_DOT: Record<string, string> = {
  info:     'bg-accent-primary',
  warning:  'bg-accent-warning',
  success:  'bg-accent-success',
  error:    'bg-accent-danger',
  deal:     'bg-blue-400',
  approval: 'bg-accent-warning',
  calendar: 'bg-accent-success',
}

export function TopBar() {
  const { isDark, toggleTheme } = useThemeStore()
  const user = useUserStore((s) => s.user)
  const { items: notifications, fetch, markRead, markAllRead, unreadCount } = useNotificationStore()
  const [bellOpen, setBellOpen] = React.useState(false)
  const [menuOpen, setMenuOpen] = React.useState(false)
  const { logout } = useAuth0()
  const navigate = useNavigate()

  useEffect(() => {
    void fetch()
    const interval = setInterval(() => void fetch(), 30_000)
    return () => clearInterval(interval)
  }, [])

  const bellRef = React.useRef<HTMLDivElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!bellOpen && !menuOpen) return
    const handler = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) setBellOpen(false)
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [bellOpen, menuOpen])

  const count = unreadCount()
  const initials = initialsForName(user?.name)

  return (
    <header className="h-16 bg-bg-secondary flex items-center px-6 shrink-0 gap-4"
      style={{ borderBottom: '1px solid rgba(30,48,92,0.1)', boxShadow: '0 1px 0 rgba(30,48,92,0.06), 0 2px 8px rgba(30,48,92,0.04)' }}>

      {/* ── Search ─────────────────────────────────────────────────────── */}
      <div className="flex-1 max-w-sm">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Search contacts, deals, properties… ⌘K"
            className="input pl-9 py-1.5 text-sm bg-bg-primary/60 border-bg-tertiary/60 focus:ring-taupe/25 focus:border-taupe/40"
          />
        </div>
      </div>

      {/* ── Right controls ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-0.5 ml-auto">

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg hover:bg-bg-tertiary/60 transition-colors text-text-muted hover:text-text-primary"
          aria-label="Toggle theme"
          title={isDark ? 'Light mode' : 'Dark mode'}
        >
          {isDark
            ? <Sun size={17} />
            : <Moon size={17} />}
        </button>

        {/* Notification bell */}
        <div className="relative" ref={bellRef}>
          <button
            className="relative p-2 rounded-lg hover:bg-bg-tertiary/60 transition-colors text-text-muted hover:text-text-primary"
            aria-label="Notifications"
            onClick={() => { setBellOpen((v) => !v); setMenuOpen(false) }}
          >
            <Bell size={17} />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1
                text-white text-[9px] font-bold rounded-full
                flex items-center justify-center leading-none"
                style={{ background: 'rgb(var(--accent-danger))' }}>
                {count > 99 ? '99+' : count}
              </span>
            )}
          </button>

          {bellOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-bg-secondary border border-bg-tertiary
              rounded-xl shadow-modal z-50 overflow-hidden"
              style={{ boxShadow: '0 8px 32px rgba(30,48,92,0.12), 0 2px 8px rgba(30,48,92,0.08)' }}>
              <div className="px-4 py-3 flex items-center justify-between"
                style={{ borderBottom: '1px solid rgba(30,48,92,0.08)', background: 'linear-gradient(to right, rgb(245,241,236), white)' }}>
                <span className="font-bold text-text-primary text-sm tracking-tight">
                  Notifications
                  {count > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold text-white rounded-full"
                      style={{ background: 'rgb(var(--accent-danger))' }}>
                      {count}
                    </span>
                  )}
                </span>
                {count > 0 && (
                  <button
                    className="text-xs text-text-muted hover:text-taupe transition-colors font-medium"
                    onClick={() => markAllRead()}
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-72 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="py-10 text-center">
                    <Bell size={24} className="mx-auto text-text-muted mb-2 opacity-30" />
                    <p className="text-sm text-text-muted">No notifications</p>
                  </div>
                ) : (
                  notifications.map((n) => (
                    <div
                      key={n.id}
                      onClick={() => { if (!n.read) markRead(n.id) }}
                      className={`px-4 py-3 flex gap-3 cursor-pointer hover:bg-bg-primary/50 transition-colors
                        ${n.read ? 'opacity-60' : ''}`}
                      style={{ borderBottom: '1px solid rgba(30,48,92,0.06)' }}
                    >
                      <span className={`flex-shrink-0 mt-1.5 w-2 h-2 rounded-full ${TYPE_DOT[n.type ?? 'info'] ?? 'bg-text-muted'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm leading-snug ${n.read ? 'text-text-secondary' : 'font-semibold text-text-primary'}`}>
                          {n.title}
                        </p>
                        {n.body && <p className="text-xs text-text-muted mt-0.5 truncate">{n.body}</p>}
                        {n.created_at && (
                          <p className="text-[10px] text-text-muted mt-1">
                            {new Date(n.created_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                      {!n.read && <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                        style={{ background: 'rgb(166,139,91)' }} />}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="w-px h-6 mx-2 bg-bg-tertiary" />

        {/* User menu */}
        <div className="relative" ref={menuRef}>
          <button
            className="flex items-center gap-2.5 py-1.5 px-2.5 rounded-lg hover:bg-bg-tertiary/50 transition-colors"
            onClick={() => { setMenuOpen((v) => !v); setBellOpen(false) }}
            aria-label="User menu"
          >
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #1E305C, #2d4a8a)', color: 'white' }}>
              {initials}
            </div>
            <div className="text-sm text-left hidden sm:block">
              <div className="font-semibold text-text-primary leading-tight text-sm">{user?.name ?? 'Agent'}</div>
              <div className="text-text-muted text-[10px] capitalize">{user?.role ?? '—'}</div>
            </div>
            <ChevronDown size={13} className="text-text-muted hidden sm:block" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-12 w-48 bg-bg-secondary border border-bg-tertiary
              rounded-xl z-50 overflow-hidden py-1"
              style={{ boxShadow: '0 8px 32px rgba(30,48,92,0.12), 0 2px 8px rgba(30,48,92,0.08)' }}>
              <button
                className="w-full text-left px-4 py-2.5 hover:bg-bg-primary text-sm
                  flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
                onClick={() => { setMenuOpen(false); void navigate({ to: '/profile' }) }}
              >
                <User size={13} /> Profile
              </button>
              {user?.role === 'admin' && (
                <button
                  className="w-full text-left px-4 py-2.5 hover:bg-bg-primary text-sm
                    flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
                  onClick={() => { setMenuOpen(false); void navigate({ to: '/admin' }) }}
                >
                  <Settings size={13} /> Admin Settings
                </button>
              )}
              <div className="my-1" style={{ borderTop: '1px solid rgba(30,48,92,0.08)' }} />
              <button
                className="w-full text-left px-4 py-2.5 hover:bg-red-50 text-sm
                  flex items-center gap-2 text-accent-danger transition-colors"
                onClick={() => {
                  useUserStore.getState().setUser(null)
                  logout({ logoutParams: { returnTo: window.location.origin } })
                }}
              >
                <LogOut size={13} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
